import React, { useState, Fragment } from 'react';
import { Dialog, Transition } from '@headlessui/react';
import { XMarkIcon, UserPlusIcon } from '@heroicons/react/24/outline';
import { toast } from 'react-hot-toast';
import { fetchWithCsrf } from '../../utils/csrf';

interface User {
  id: number;
  email: string;
  first_name: string;
  last_name: string;
}

interface CreateFreeAccountModalProps {
  user: User | null;
  isOpen: boolean;
  onClose: () => void;
  onSuccess: () => void;
}

type AccountType = 'friend' | 'leader' | 'potential_referral_partner' | 'current_referral_partner';

interface AccountTypeOption {
  value: AccountType;
  label: string;
  description: string;
  icon: string;
  color: string;
}

const accountTypes: AccountTypeOption[] = [
  {
    value: 'friend',
    label: 'Friend',
    description: 'Personal friend or family member',
    icon: '👋',
    color: 'blue'
  },
  {
    value: 'leader',
    label: 'Leader',
    description: 'Industry leader or influencer',
    icon: '⭐',
    color: 'purple'
  },
  {
    value: 'potential_referral_partner',
    label: 'Potential Referral Partner',
    description: 'Someone being evaluated as a partner',
    icon: '🤝',
    color: 'yellow'
  },
  {
    value: 'current_referral_partner',
    label: 'Current Referral Partner',
    description: 'Active referral partner',
    icon: '💼',
    color: 'green'
  }
];

const CreateFreeAccountModal: React.FC<CreateFreeAccountModalProps> = ({
  user,
  isOpen,
  onClose,
  onSuccess
}) => {
  const [selectedType, setSelectedType] = useState<AccountType | null>(null);
  const [reason, setReason] = useState('');
  const [notes, setNotes] = useState('');
  const [expiresAt, setExpiresAt] = useState('');
  const [limitWebsites, setLimitWebsites] = useState('3');
  const [limitAnalyses, setLimitAnalyses] = useState('50');
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleClose = () => {
    if (!isSubmitting) {
      setSelectedType(null);
      setReason('');
      setNotes('');
      setExpiresAt('');
      setLimitWebsites('3');
      setLimitAnalyses('50');
      onClose();
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!selectedType) {
      toast.error('Please select an account type');
      return;
    }

    if (!user) {
      toast.error('No user selected');
      return;
    }

    setIsSubmitting(true);

    try {
      // Map frontend type to backend type
      let backendType = selectedType;
      if (selectedType === 'potential_referral_partner' || selectedType === 'current_referral_partner') {
        backendType = 'referral_partner' as AccountType;
      }

      const requestData = {
        user_id: user.id,
        account_type: backendType,
        reason: reason || `${accountTypes.find(t => t.value === selectedType)?.label} account`,
        notes: notes || undefined,
        expires_at: expiresAt || undefined,
        limits: {
          websites: limitWebsites,
          analyses_per_month: limitAnalyses
        },
        is_active: true
      };

      const response = await fetchWithCsrf('/api/admin/free-accounts', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-Tenant-ID': '1'
        },
        body: JSON.stringify(requestData)
      });

      // Check if response is JSON before parsing
      const contentType = response.headers.get('content-type');
      if (!contentType || !contentType.includes('application/json')) {
        const textResponse = await response.text();
        console.error('Non-JSON response:', textResponse);
        toast.error(`Server error: Expected JSON but got ${contentType || 'unknown type'}. Check console for details.`);
        return;
      }

      const data = await response.json();

      if (response.ok && data.success) {
        toast.success('Free account created successfully');
        handleClose();
        onSuccess();
      } else {
        // Show validation errors if available
        if (data.errors) {
          const errorMessages = Object.values(data.errors).flat();
          toast.error(errorMessages.join(', ') || data.message || 'Failed to create free account');
        } else {
          toast.error(data.message || data.error || 'Failed to create free account');
        }
        console.error('API Error:', data);
      }
    } catch (error) {
      console.error('Error creating free account:', error);
      toast.error('Network error: ' + (error instanceof Error ? error.message : 'Unknown error'));
    } finally {
      setIsSubmitting(false);
    }
  };

  const getColorClasses = (color: string) => {
    const colors: Record<string, string> = {
      blue: 'border-blue-500/30 bg-blue-500/10 hover:bg-blue-500/20',
      purple: 'border-purple-500/30 bg-purple-500/10 hover:bg-purple-500/20',
      yellow: 'border-yellow-500/30 bg-yellow-500/10 hover:bg-yellow-500/20',
      green: 'border-green-500/30 bg-green-500/10 hover:bg-green-500/20'
    };
    return colors[color] || colors.blue;
  };

  const getSelectedColorClasses = (color: string) => {
    const colors: Record<string, string> = {
      blue: 'border-blue-500 bg-blue-500/20 ring-2 ring-blue-500/50',
      purple: 'border-purple-500 bg-purple-500/20 ring-2 ring-purple-500/50',
      yellow: 'border-yellow-500 bg-yellow-500/20 ring-2 ring-yellow-500/50',
      green: 'border-green-500 bg-green-500/20 ring-2 ring-green-500/50'
    };
    return colors[color] || colors.blue;
  };

  return (
    <Transition appear show={isOpen} as={Fragment}>
      <Dialog as="div" className="relative z-50" onClose={handleClose}>
        <Transition.Child
          as={Fragment}
          enter="ease-out duration-300"
          enterFrom="opacity-0"
          enterTo="opacity-100"
          leave="ease-in duration-200"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
        >
          <div className="fixed inset-0 bg-black/60 backdrop-blur-sm" />
        </Transition.Child>

        <div className="fixed inset-0 overflow-y-auto">
          <div className="flex min-h-full items-center justify-center p-4 text-center">
            <Transition.Child
              as={Fragment}
              enter="ease-out duration-300"
              enterFrom="opacity-0 scale-95"
              enterTo="opacity-100 scale-100"
              leave="ease-in duration-200"
              leaveFrom="opacity-100 scale-100"
              leaveTo="opacity-0 scale-95"
            >
              <Dialog.Panel className="w-full max-w-2xl transform overflow-hidden rounded-2xl bg-slate-800 border border-slate-700 p-6 text-left align-middle shadow-xl transition-all">
                {/* Header */}
                <div className="flex items-center justify-between mb-6">
                  <div className="flex items-center space-x-3">
                    <div className="p-2 bg-gradient-to-br from-indigo-500 to-purple-600 rounded-lg">
                      <UserPlusIcon className="h-6 w-6 text-white" />
                    </div>
                    <div>
                      <Dialog.Title className="text-xl font-bold text-white">
                        Create Free Account
                      </Dialog.Title>
                      {user && (
                        <p className="text-sm text-slate-400 mt-0.5">
                          for {user.first_name} {user.last_name} ({user.email})
                        </p>
                      )}
                    </div>
                  </div>
                  <button
                    onClick={handleClose}
                    className="text-slate-400 hover:text-white transition-colors"
                    disabled={isSubmitting}
                  >
                    <XMarkIcon className="h-6 w-6" />
                  </button>
                </div>

                <form onSubmit={handleSubmit}>
                  {/* Account Type Selection */}
                  <div className="mb-6">
                    <label className="block text-sm font-medium text-slate-300 mb-3">
                      What type of Free Account is this? *
                    </label>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                      {accountTypes.map((type) => (
                        <button
                          key={type.value}
                          type="button"
                          onClick={() => setSelectedType(type.value)}
                          className={`relative p-4 rounded-xl border-2 transition-all duration-200 text-left ${
                            selectedType === type.value
                              ? getSelectedColorClasses(type.color)
                              : `border-slate-700 bg-slate-900/50 hover:bg-slate-900 ${getColorClasses(type.color)}`
                          }`}
                          disabled={isSubmitting}
                        >
                          <div className="flex items-start space-x-3">
                            <span className="text-3xl">{type.icon}</span>
                            <div className="flex-1">
                              <div className="font-medium text-white mb-1">
                                {type.label}
                              </div>
                              <div className="text-xs text-slate-400">
                                {type.description}
                              </div>
                            </div>
                          </div>
                          {selectedType === type.value && (
                            <div className="absolute top-3 right-3">
                              <div className="bg-white rounded-full p-0.5">
                                <svg className="w-4 h-4 text-green-600" fill="currentColor" viewBox="0 0 20 20">
                                  <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                                </svg>
                              </div>
                            </div>
                          )}
                        </button>
                      ))}
                    </div>
                  </div>

                  {/* Reason */}
                  <div className="mb-4">
                    <label className="block text-sm font-medium text-slate-300 mb-2">
                      Reason (optional)
                    </label>
                    <input
                      type="text"
                      value={reason}
                      onChange={(e) => setReason(e.target.value)}
                      placeholder="Why are you granting a free account?"
                      className="w-full px-4 py-2 bg-slate-900/50 border border-slate-700 rounded-lg text-white placeholder-slate-500 focus:border-indigo-500 focus:ring-1 focus:ring-indigo-500/20"
                      disabled={isSubmitting}
                    />
                  </div>

                  {/* Limits */}
                  <div className="grid grid-cols-2 gap-4 mb-4">
                    <div>
                      <label className="block text-sm font-medium text-slate-300 mb-2">
                        Website Limit
                      </label>
                      <input
                        type="number"
                        value={limitWebsites}
                        onChange={(e) => setLimitWebsites(e.target.value)}
                        min="1"
                        className="w-full px-4 py-2 bg-slate-900/50 border border-slate-700 rounded-lg text-white focus:border-indigo-500 focus:ring-1 focus:ring-indigo-500/20"
                        disabled={isSubmitting}
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-slate-300 mb-2">
                        Analyses Per Month
                      </label>
                      <input
                        type="number"
                        value={limitAnalyses}
                        onChange={(e) => setLimitAnalyses(e.target.value)}
                        min="1"
                        className="w-full px-4 py-2 bg-slate-900/50 border border-slate-700 rounded-lg text-white focus:border-indigo-500 focus:ring-1 focus:ring-indigo-500/20"
                        disabled={isSubmitting}
                      />
                    </div>
                  </div>

                  {/* Expiration Date */}
                  <div className="mb-4">
                    <label className="block text-sm font-medium text-slate-300 mb-2">
                      Expiration Date (optional)
                    </label>
                    <input
                      type="date"
                      value={expiresAt}
                      onChange={(e) => setExpiresAt(e.target.value)}
                      className="w-full px-4 py-2 bg-slate-900/50 border border-slate-700 rounded-lg text-white focus:border-indigo-500 focus:ring-1 focus:ring-indigo-500/20"
                      disabled={isSubmitting}
                    />
                    <p className="text-xs text-slate-500 mt-1">
                      Leave empty for no expiration
                    </p>
                  </div>

                  {/* Notes */}
                  <div className="mb-6">
                    <label className="block text-sm font-medium text-slate-300 mb-2">
                      Notes (optional)
                    </label>
                    <textarea
                      value={notes}
                      onChange={(e) => setNotes(e.target.value)}
                      placeholder="Additional notes or context..."
                      rows={3}
                      className="w-full px-4 py-2 bg-slate-900/50 border border-slate-700 rounded-lg text-white placeholder-slate-500 focus:border-indigo-500 focus:ring-1 focus:ring-indigo-500/20"
                      disabled={isSubmitting}
                    />
                  </div>

                  {/* Actions */}
                  <div className="flex justify-end space-x-3">
                    <button
                      type="button"
                      onClick={handleClose}
                      className="px-4 py-2 text-sm font-medium text-slate-300 bg-slate-700/50 hover:bg-slate-700 rounded-lg transition-colors"
                      disabled={isSubmitting}
                    >
                      Cancel
                    </button>
                    <button
                      type="submit"
                      className="px-4 py-2 text-sm font-medium text-white bg-gradient-to-br from-indigo-500 to-purple-600 hover:from-indigo-600 hover:to-purple-700 rounded-lg transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed"
                      disabled={isSubmitting || !selectedType}
                    >
                      {isSubmitting ? (
                        <span className="flex items-center">
                          <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" fill="none" viewBox="0 0 24 24">
                            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                          </svg>
                          Creating...
                        </span>
                      ) : (
                        'Create Free Account'
                      )}
                    </button>
                  </div>
                </form>
              </Dialog.Panel>
            </Transition.Child>
          </div>
        </div>
      </Dialog>
    </Transition>
  );
};

export default CreateFreeAccountModal;
